import {
  usePrivy,
  User,
  useWallets,
  WalletWithMetadata,
} from '@privy-io/react-auth';
import { useConnectedStandardWallets } from '@privy-io/react-auth/solana';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  clearPrimaryPrivateApiHeaders,
  clearPrivateApiHeaders,
} from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { useCapitalStore } from '@/store/use-capital-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { useTransfersStore } from '@/store/use-transfers-store';
import { EventType, LPOrderType, NetworkType, OrderStatus } from '@/types';
import { sendEvent } from '@/utils/analytics';

export const usePrivyAccount = () => {
  const { user, authenticated, ready, logout } = usePrivy();
  const { ready: walletsReady, wallets: evmWallets } = useWallets();
  const { ready: solanaWalletsReady, wallets: solanaWallets } =
    useConnectedStandardWallets();
  const queryClient = useQueryClient();

  const address = useAccountStore((state) => state.address);
  const capitalStatusByAddress = useAccountStore(
    useShallow((state) => state.capitalStatusByAddress),
  );

  const primaryAddress = useMemo(
    () => getPrivyPrimaryAddress(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.wallet?.address, user?.linkedAccounts, capitalStatusByAddress],
  );

  const isPrimaryEmbedded = useMemo(() => {
    if (!primaryAddress || !user?.linkedAccounts) return false;
    return (
      (
        user.linkedAccounts.find(
          (acc) => acc.type === 'wallet' && acc.address === primaryAddress,
        ) as WalletWithMetadata | undefined
      )?.walletClientType === 'privy'
    );
  }, [primaryAddress, user?.linkedAccounts]);

  const activeWallet = useMemo(() => {
    if (!address || !user?.linkedAccounts?.length) {
      return undefined;
    }

    return user.linkedAccounts.find(
      (acc) => acc.type === 'wallet' && acc.address === address,
    ) as WalletWithMetadata | undefined;
  }, [address, user?.linkedAccounts]);

  const networkType =
    activeWallet?.chainType === 'solana' ? NetworkType.SOLANA : NetworkType.EVM;
  const isEmbedded = activeWallet?.walletClientType === 'privy';
  const isPrimary = !!address && address === primaryAddress;
  const email = user?.email?.address ?? user?.google?.email;
  const hasEmail = !!email;
  const isReady = ready && walletsReady && solanaWalletsReady;

  async function disconnect(options?: {
    reload?: boolean;
    onSuccess?: () => void;
  }) {
    await logout();

    const { setHasAuth, setHasPrimaryAuth, clearAccountStore } =
      useAccountStore.getState();

    clearPrivateApiHeaders(['X-API-KEY']);
    clearPrimaryPrivateApiHeaders(['X-API-KEY']);
    clearAccountStore();
    const { clearTransfersStore } = useTransfersStore.getState();
    const { clearOrdersStore } = useOrdersStore.getState();
    const { clearCapitalStore } = useCapitalStore.getState();
    clearTransfersStore();
    clearOrdersStore();
    clearCapitalStore();
    setHasAuth(false);
    setHasPrimaryAuth(false);

    queryClient.removeQueries({
      queryKey: [
        'lpOrders',
        { orderType: LPOrderType.SCHEDULE_WITHDRAW, status: OrderStatus.NEW },
      ],
      exact: true,
    });

    sendEvent(EventType.LOG_OUT, { email });

    options?.onSuccess?.();

    if (options?.reload) {
      window.location.reload();
    }
  }

  return {
    isPrimary,
    address: primaryAddress,
    activeWallet,
    isEmbedded,
    isPrimaryEmbedded,
    email,
    hasEmail,
    authenticated,
    isConnected: isReady && authenticated,
    privyReady:
      ready && (!authenticated || (walletsReady && solanaWalletsReady)),
    evmWallets,
    solanaWallets,
    networkType,
    isReady,
    disconnect,
  };
};

export const getPrivyPrimaryAddress = (
  user?: User | null,
): string | undefined => {
  if (!user || !user?.linkedAccounts?.length) return;

  let account: WalletWithMetadata | undefined;

  for (const acc of user.linkedAccounts) {
    if (acc.type !== 'wallet') continue;
    if (acc.chainType === 'solana') return acc.address;
    if (
      !account ||
      !account.firstVerifiedAt ||
      (acc.firstVerifiedAt &&
        acc.firstVerifiedAt.getTime() < account.firstVerifiedAt.getTime())
    ) {
      account = acc;
    }
  }

  if (!account) {
    if (user.wallet) {
      account = user.wallet as WalletWithMetadata;
    } else {
      account = user.linkedAccounts.find((acc) => acc.type === 'wallet');
    }
  }

  return account?.address;
};
